import { InputState, Rectangle } from '../../types/game.types';
import { BaseGame } from '../BaseGame';
import { RenderManager } from '../../core/RenderManager';
import { AudioManager } from '../../core/AudioManager';
import { DefenderShip } from './entities/DefenderShip';
import { Humanoid } from './entities/Humanoid';
import { Lander } from './entities/Lander';
import { Bullet, BulletType } from './entities/Bullet';
import { Terrain } from './systems/Terrain';
import { Scanner } from './systems/Scanner';
import { EnemyManager } from './systems/EnemyManager';

export class DefenderGame extends BaseGame {
  private ship: DefenderShip | null = null;
  private bullets: Bullet[] = [];
  private humanoids: Humanoid[] = [];
  private enemies: any[] = []; // Will be managed by EnemyManager
  private terrain: Terrain;
  private scanner: Scanner;
  private enemyManager: EnemyManager;
  private nextBulletId: number = 1;
  
  // World properties
  private worldWidth: number = 6000; // Much wider than screen
  private cameraX: number = 0; // Camera position in world
  private cameraSpeed: number = 200; // Camera follow speed
  
  // Game state
  private gameStarted: boolean = false;
  private waveComplete: boolean = false;
  private humanoidsSaved: number = 0;
  private hyperspaceCharges: number = 3;

  constructor(renderManager: RenderManager, audioManager: AudioManager) {
    const gameConfig = { width: 900, height: 600, fps: 60, scale: 1 }; // Landscape for mobile
    super(renderManager, audioManager, gameConfig, 'defender');
    
    this.terrain = new Terrain(this.worldWidth, this.config.height);
    this.scanner = new Scanner(this.config.width, this.worldWidth);
    this.enemyManager = new EnemyManager(this.worldWidth, renderManager, audioManager);
  }

  public init(): void {
    console.log('Defender Game initialized');
    
    // Reset game state
    this.score = 0;
    this.lives = 3;
    this.level = 1;
    this.isGameOver = false;
    this.gameStarted = true;
    this.waveComplete = false;
    this.humanoidsSaved = 0;
    this.hyperspaceCharges = 3;
    this.cameraX = 0;
    
    // Clear arrays
    this.bullets = [];
    this.humanoids = [];
    this.enemies = [];
    this.nextBulletId = 1;
    
    // Create player ship
    this.createShip();
    
    // Create humanoids on ground
    this.createHumanoids();
    
    // Initialize enemies for this wave
    this.enemyManager.initializeWave(this.level);
    this.enemies = this.enemyManager.getEnemies();
  }

  private createShip(): void {
    // Start ship in middle of world, above ground
    const startX = this.worldWidth / 2;
    const startY = this.terrain.getGroundHeight(startX) - 100;
    
    this.ship = new DefenderShip(
      'defender_ship',
      startX,
      startY,
      this.renderManager,
      this.audioManager,
      this.worldWidth
    );
  }

  private createHumanoids(): void {
    const numHumanoids = 10; // Classic Defender has 10 humanoids
    
    for (let i = 0; i < numHumanoids; i++) {
      const x = (i * (this.worldWidth / numHumanoids)) + (this.worldWidth / numHumanoids / 2);
      const y = this.terrain.getGroundHeight(x) - 20;
      
      const humanoid = new Humanoid(
        `humanoid_${i}`,
        x,
        y,
        this.renderManager,
        this.audioManager
      );
      
      this.humanoids.push(humanoid);
    }
  }

  public update(deltaTime: number): void {
    if (this.isGameOver) return;
    
    if (!this.gameStarted || !this.ship) return;
    
    // Update ship
    if (this.ship.active) {
      this.ship.setCameraX(this.cameraX);
      this.ship.update(deltaTime);
    }
    
    // Update camera to follow ship
    this.updateCamera(deltaTime);
    
    // Update bullets
    this.updateBullets(deltaTime);
    
    // Update humanoids
    this.updateHumanoids(deltaTime);
    
    // Update enemies
    this.updateEnemies(deltaTime);
    
    // Check collisions
    this.checkCollisions();
    
    // Check win/lose conditions
    this.checkWaveConditions();
  }

  private updateCamera(deltaTime: number): void {
    if (!this.ship) return;
    
    const shipScreenX = this.ship.position.x - this.cameraX;
    const centerX = this.config.width / 2;
    
    // Keep ship roughly centered, but allow some drift
    if (Math.abs(shipScreenX - centerX) > 100) {
      const targetCameraX = this.ship.position.x - centerX;
      const cameraMove = (targetCameraX - this.cameraX) * deltaTime * 2;
      this.cameraX += cameraMove;
      
      // Clamp camera to world bounds
      this.cameraX = Math.max(0, Math.min(this.worldWidth - this.config.width, this.cameraX));
    }
  }

  private updateBullets(deltaTime: number): void {
    this.bullets.forEach(bullet => {
      bullet.setCameraX(this.cameraX);
      bullet.update(deltaTime);
    });
    this.bullets = this.bullets.filter(bullet => bullet.active);
  }

  private updateHumanoids(deltaTime: number): void {
    this.humanoids.forEach(humanoid => {
      if (humanoid.active) {
        humanoid.setTerrain(this.terrain);
        humanoid.setCameraX(this.cameraX);
        humanoid.update(deltaTime);
      }
    });
  }

  private updateEnemies(deltaTime: number): void {
    this.enemyManager.update(deltaTime, this.humanoids, this.ship);
    this.enemies = this.enemyManager.getEnemies();
    
    // Set camera position for all enemies
    this.enemies.forEach(enemy => {
      if (enemy.setCameraX) {
        enemy.setCameraX(this.cameraX);
      }
    });
  }

  private checkCollisions(): void {
    if (!this.ship || !this.ship.active) return;

    // Check bullet vs enemy collisions
    this.bullets.forEach(bullet => {
      if (!bullet.active || bullet.getType() !== 'player') return;

      this.enemies.forEach(enemy => {
        if (!enemy.active) return;

        if (this.checkRectCollision(bullet.getBounds(), enemy.getBounds())) {
          // Bullet hit enemy
          bullet.destroy();
          
          // Handle different enemy types
          if (enemy.type === 'lander') {
            // If lander was abducting, release humanoid
            if (enemy.isAbducting && typeof enemy.isAbducting === 'function' && enemy.isAbducting()) {
              enemy.releaseHumanoid();
            }
            
            // Award points
            const points = enemy.getPointValue ? enemy.getPointValue() : 150;
            this.addScore(points);
            
            // Destroy enemy
            enemy.destroy();
            this.audioManager.playExplosion();
          }
        }
      });
    });

    // Check ship vs enemy collisions
    this.enemies.forEach(enemy => {
      if (!enemy.active) return;

      if (this.checkRectCollision(this.ship!.getBounds(), enemy.getBounds())) {
        // Ship hit enemy - lose life
        this.loseLife();
        enemy.destroy();
        this.audioManager.playExplosion();
      }
    });

    // Check enemy bullets vs ship
    this.bullets.forEach(bullet => {
      if (!bullet.active || bullet.getType() !== 'enemy') return;

      if (this.checkRectCollision(bullet.getBounds(), this.ship!.getBounds())) {
        // Enemy bullet hit ship
        bullet.destroy();
        this.loseLife();
      }
    });

    // Check ship vs humanoid (rescue mechanic)
    this.humanoids.forEach(humanoid => {
      if (!humanoid.active || humanoid.getState() !== 'falling') return;

      if (this.checkRectCollision(this.ship!.getBounds(), humanoid.getBounds())) {
        // Rescue humanoid
        humanoid.rescue();
        this.addScore(250); // Rescue bonus
        this.audioManager.playPowerUp();
      }
    });
  }

  private checkRectCollision(rect1: Rectangle, rect2: Rectangle): boolean {
    return rect1.x < rect2.x + rect2.width &&
           rect1.x + rect1.width > rect2.x &&
           rect1.y < rect2.y + rect2.height &&
           rect1.y + rect1.height > rect2.y;
  }

  private checkWaveConditions(): void {
    // Check if all enemies are destroyed AND no more enemies to spawn
    const activeEnemies = this.enemies.filter(enemy => enemy.active);
    const waveFinished = this.enemyManager.isWaveComplete();
    
    // Debug logging
    if (!this.waveComplete) {
      console.log(`Wave check: activeEnemies=${activeEnemies.length}, waveFinished=${waveFinished}, enemyManagerCount=${this.enemyManager.getActiveEnemyCount()}`);
    }
    
    if (waveFinished && !this.waveComplete) {
      this.waveComplete = true;
      this.completeWave();
    }
    
    // Check if all humanoids are dead (game over condition)
    const aliveHumanoids = this.humanoids.filter(h => h.active && !h.isAbducted());
    if (aliveHumanoids.length === 0) {
      this.gameOver();
    }
  }

  private completeWave(): void {
    console.log(`Wave ${this.level} complete!`);
    
    // Bonus points for surviving humanoids
    const survivingHumanoids = this.humanoids.filter(h => h.active && !h.isAbducted()).length;
    const bonus = survivingHumanoids * 100;
    this.addScore(bonus);
    
    // Start next wave after delay
    setTimeout(() => {
      this.nextLevel();
      this.startNextWave();
    }, 2000);
  }

  private startNextWave(): void {
    this.waveComplete = false;
    this.hyperspaceCharges = 3; // Restore hyperspace charges
    
    // Revive humanoids if needed (classic Defender behavior)
    if (this.humanoids.filter(h => h.active).length < 5) {
      this.createHumanoids();
    }
    
    // Initialize enemies for new wave
    this.enemyManager.initializeWave(this.level);
    this.enemies = this.enemyManager.getEnemies();
  }

  public render(ctx: CanvasRenderingContext2D): void {
    // Clear screen with space background
    this.renderManager.clear('#000000');
    
    // Render terrain
    this.terrain.render(ctx, this.cameraX);
    
    // Render humanoids (only visible ones)
    this.humanoids.forEach(humanoid => {
      if (this.isInView(humanoid.position.x)) {
        humanoid.render(ctx);
      }
    });
    
    // Render enemies (only visible ones)
    this.enemies.forEach(enemy => {
      if (enemy.active && this.isInView(enemy.position.x)) {
        enemy.render(ctx);
      }
    });
    
    // Render bullets (only visible ones)
    this.bullets.forEach(bullet => {
      if (bullet.active && this.isInView(bullet.position.x)) {
        bullet.render(ctx);
      }
    });
    
    // Render ship
    if (this.ship && this.ship.active) {
      this.ship.render(ctx);
    }
    
    // Render scanner
    this.scanner.render(ctx, this.ship, this.enemies, this.humanoids);
    
    // Render HUD
    this.renderHUD();
    
    // Render game state messages
    if (this.waveComplete) {
      this.renderWaveComplete();
    }
    
    if (this.isGameOver) {
      this.renderGameOver();
    }
    
    // Render initial entry if showing
    if (this.showingInitialEntry && this.initialEntry) {
      this.initialEntry.render(ctx, this.config.width / 2, this.config.height / 2);
    }
  }

  private isInView(worldX: number): boolean {
    return worldX >= this.cameraX - 100 && worldX <= this.cameraX + this.config.width + 100;
  }

  protected renderHUD(): void {
    // Score
    this.renderManager.drawText(
      `SCORE: ${this.score.toString().padStart(6, '0')}`,
      20,
      20,
      '#FFFFFF',
      '16px "Courier New", monospace'
    );
    
    // Lives
    this.renderManager.drawText('LIVES: ', this.config.width - 200, 20, '#FFFFFF', '16px "Courier New", monospace');
    for (let i = 0; i < this.lives; i++) {
      this.renderManager.drawC64Char('▲', this.config.width - 120 + i * 20, 18, '#62D0FE', 1);
    }
    
    // Wave
    this.renderManager.drawTextCentered(
      `WAVE ${this.level}`,
      this.config.width / 2,
      20,
      '#BFCE72',
      '16px "Courier New", monospace'
    );
    
    // Humanoids remaining
    const aliveHumanoids = this.humanoids.filter(h => h.active && !h.isAbducted()).length;
    this.renderManager.drawText(
      `HUMANS: ${aliveHumanoids}`,
      20,
      this.config.height - 40,
      '#55A049',
      '12px "Courier New", monospace'
    );
    
    // Hyperspace charges
    this.renderManager.drawText(
      `HYPER: ${this.hyperspaceCharges}`,
      20,
      this.config.height - 30,
      '#FFA500',
      '12px "Courier New", monospace'
    );
  }

  private renderWaveComplete(): void {
    const centerX = this.config.width / 2;
    const centerY = this.config.height / 2;
    
    this.renderManager.fillRect(0, 0, this.config.width, this.config.height, 'rgba(0, 0, 0, 0.7)');
    
    this.renderManager.drawTextCentered(
      `WAVE ${this.level} COMPLETE!`,
      centerX,
      centerY - 40,
      '#55A049',
      '32px "Courier New", monospace'
    );
    
    const survivingHumanoids = this.humanoids.filter(h => h.active && !h.isAbducted()).length;
    const bonus = survivingHumanoids * 100;
    
    this.renderManager.drawTextCentered(
      `HUMANS SAVED: ${survivingHumanoids}`,
      centerX,
      centerY,
      '#BFCE72',
      '18px "Courier New", monospace'
    );
    
    this.renderManager.drawTextCentered(
      `BONUS: ${bonus}`,
      centerX,
      centerY + 40,
      '#FFA500',
      '18px "Courier New", monospace'
    );
  }

  public handleInput(input: InputState): void {
    if (this.isGameOver) {
      if (this.showingInitialEntry) {
        return;
      }
      
      if (input.keys['Space'] || input.touch.gesture === 'tap') {
        this.restart();
      }
      return;
    }
    
    if (!this.ship || !this.ship.active) return;
    
    // Ship movement (thrust-based)
    if (input.keys['ArrowLeft'] || input.keys['KeyA']) {
      this.ship.thrustLeft();
    }
    if (input.keys['ArrowRight'] || input.keys['KeyD']) {
      this.ship.thrustRight();
    }
    if (input.keys['ArrowUp'] || input.keys['KeyW']) {
      this.ship.thrustUp();
    }
    if (input.keys['ArrowDown'] || input.keys['KeyS']) {
      this.ship.thrustDown();
    }
    
    // Reverse (instant direction flip)
    if (input.keys['KeyR']) {
      this.ship.reverse();
    }
    
    // Shooting
    if (input.keys['Space'] || input.touch.gesture === 'tap') {
      const bulletPos = this.ship.fireBullet();
      if (bulletPos) {
        this.createPlayerBullet(bulletPos.x, bulletPos.y, bulletPos.direction);
      }
    }
    
    // Hyperspace
    if (input.keys['KeyH'] && this.hyperspaceCharges > 0) {
      this.useHyperspace();
    }
  }

  private createPlayerBullet(x: number, y: number, direction: number): void {
    const bullet = new Bullet(
      `bullet_player_${this.nextBulletId++}`,
      x,
      y,
      BulletType.PLAYER,
      direction,
      this.renderManager,
      this.worldWidth
    );
    this.bullets.push(bullet);
  }

  private useHyperspace(): void {
    if (!this.ship) return;
    
    this.hyperspaceCharges--;
    
    // Move ship to random location
    const newX = Math.random() * this.worldWidth;
    const newY = Math.random() * (this.config.height - 200) + 100;
    
    this.ship.hyperspace(newX, newY);
    this.audioManager.playPowerUp(); // Placeholder sound
    
    console.log(`Hyperspace used! Charges remaining: ${this.hyperspaceCharges}`);
  }

  public cleanup(): void {
    super.cleanup();
    this.bullets = [];
    this.humanoids = [];
    this.enemies = [];
    this.ship = null;
  }
}